import { Dispatch, useEffect, useState } from "react";

import { isBrowser } from "@zapier/reporting/src/utils";

const parseReadValue = (value: string) => {
  try {
    return JSON.parse(value);
  } catch (e) {
    console.warn(`Could not parse value read from sessionStorage.`, e);
  }
  return value;
};

const readValue = (key: string) => {
  if (isBrowser()) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      console.warn(`Could not read "${key}" from sessionStorage.`, e);
    }
  }
  return undefined;
};

const stringifyWriteValue = (value: any) => {
  try {
    return value && value instanceof Object ? JSON.stringify(value) : value;
  } catch (e) {
    console.warn(
      `Could not stringify value before writing it to sessionStorage.`,
      e
    );
  }
  return value;
};

const writeValue = (key: string, value: string) => {
  if (!isBrowser()) {
    return;
  }

  try {
    if (value === null || value === undefined) {
      window.sessionStorage.removeItem(key);
      return;
    }

    window.sessionStorage.setItem(key, value);
  } catch (e) {
    console.warn(`Could not write "${key}" to sessionStorage.`, e);
  }
};

const useSessionStorage = <T = any>(
  key: string,
  initialValue?: T
): [T | undefined, Dispatch<T | undefined>] => {
  // Use `useState` to prevent `sessionStorage` from being
  // accessed on every rerun of this hook.
  const [value, setValue] = useState(() => {
    const rawValue = readValue(key);
    return rawValue ? parseReadValue(rawValue) : initialValue;
  });

  useEffect(() => {
    const rawValue = readValue(key);
    setValue(rawValue ? parseReadValue(rawValue) : initialValue);
    // Grandfathered in on 26-08-2024, OB-1373
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const stringifiedValue = stringifyWriteValue(value);
    writeValue(key, stringifiedValue);
  }, [key, value]);
  return [value, setValue];
};

export default useSessionStorage;
